import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  ListChecks, Plus, X, ChevronUp, ChevronDown, Loader2, RotateCcw, Undo2,
  ArrowRight, AlertTriangle, Palette, Users, Wand2, FileText, Zap,
} from 'lucide-react';

// ─── Plan mode ────────────────────────────────────────────────────────────────
// The step between "describe it" and "build it". A full build is tens of
// thousands of tokens and a credit; a plan is a few hundred and free, so this
// is where a site should be got right.
//
// What it replaced did three things wrong, which is why people said plan mode
// "did not work":
//   - If the plan could not be drafted, the build quietly started anyway — so
//     with Plan mode on, people were charged for a site they never reviewed.
//     A failure is now said, with Try again and Build without a plan.
//   - A six-second countdown started the build on its own. Nobody can read
//     and correct a five-page plan in six seconds. It waits for Build now.
//   - It was a card in a 220px chat column where only page names could be
//     edited. It now has the whole workbench: every page, section, colour and
//     feature is editable, and the plan can be revised by describing the
//     change ("add a pricing page") — free, as many times as it takes.

const SECTION_LIMIT = 8;
const PAGE_LIMIT = 6;
const FEATURE_LIMIT = 6;

const DRAFT_STEPS = ['Reading your idea', 'Choosing the pages', 'Laying out each page', 'Picking a style'];

function slugPath(label, taken) {
  const base = String(label || 'page').toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'page';
  let path = `${base}.html`;
  let n = 2;
  while (taken.has(path) || path === 'index.html') { path = `${base}-${n}.html`; n += 1; }
  return path;
}

function ChipInput({ placeholder, onAdd, label, disabled = false }) {
  const [value, setValue] = useState('');
  const add = () => {
    const v = value.trim();
    if (!v) return;
    onAdd(v);
    setValue('');
  };
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-zinc-700 pl-2.5 pr-1 h-7">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value.slice(0, 60))}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        placeholder={placeholder}
        aria-label={label}
        disabled={disabled}
        className="w-28 bg-transparent text-xs text-zinc-200 placeholder-zinc-600 outline-none"
      />
      <button type="button" onClick={add} disabled={disabled || !value.trim()} aria-label={`Add ${label.toLowerCase()}`}
        className="w-5 h-5 rounded-full grid place-items-center text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-40">
        <Plus className="w-3 h-3" />
      </button>
    </span>
  );
}

function Chip({ children, onRemove, removeLabel }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-zinc-800 border border-zinc-700 pl-2.5 pr-1 h-7 text-xs text-zinc-200 max-w-full">
      <span className="truncate">{children}</span>
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label={removeLabel}
          className="w-5 h-5 rounded-full grid place-items-center text-zinc-500 hover:text-red-400 hover:bg-zinc-900 shrink-0">
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

/** What the screen shows while the plan is being drafted — the real steps, not a spinner. */
function Drafting({ prompt }) {
  const [step, setStep] = useState(0);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (reduceMotion) return undefined;
    const t = setInterval(() => setStep(s => Math.min(s + 1, DRAFT_STEPS.length - 1)), 1600);
    return () => clearInterval(t);
  }, [reduceMotion]);

  return (
    <div className="max-w-3xl mx-auto w-full" role="status" aria-live="polite">
      <div className="flex items-center gap-2 text-xs font-semibold text-orange-300">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/10 border border-orange-500/25 px-2.5 py-1">
          <ListChecks className="w-3.5 h-3.5" /> Plan mode
        </span>
        <span className="text-zinc-500 font-normal">Free — nothing is built yet</span>
      </div>
      <h2 className="mt-4 text-2xl sm:text-3xl font-extrabold tracking-tight text-white">Drafting your plan…</h2>
      {prompt && <p className="mt-2 text-sm text-zinc-500 line-clamp-2">“{prompt}”</p>}
      <ol className="mt-6 space-y-2.5">
        {DRAFT_STEPS.map((label, i) => (
          <li key={label} className="flex items-center gap-2.5 text-sm">
            {i < step ? <span className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/40 grid place-items-center text-emerald-400 text-[10px]">✓</span>
              : i === step ? <Loader2 className="w-5 h-5 text-orange-400 animate-spin" />
                : <span className="w-5 h-5 rounded-full border border-zinc-700" />}
            <span className={i <= step ? 'text-zinc-200' : 'text-zinc-600'}>{label}</span>
          </li>
        ))}
      </ol>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3" aria-hidden="true">
        {[0, 1, 2].map(i => (
          <div key={i} className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 space-y-2.5 animate-pulse">
            <div className="h-3 w-1/2 rounded bg-zinc-800" />
            <div className="h-2.5 w-full rounded bg-zinc-800/80" />
            <div className="h-2.5 w-4/5 rounded bg-zinc-800/80" />
            <div className="flex gap-1.5 pt-1"><div className="h-5 w-12 rounded-full bg-zinc-800" /><div className="h-5 w-16 rounded-full bg-zinc-800" /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {object|null} props.plan
 * @param {'drafting'|'ready'|'revising'|'error'} props.status
 * @param {string} [props.error]
 * @param {string} [props.prompt]     the request the plan is for
 * @param {number|null} [props.credits] credits left, for the footer
 * @param {boolean} [props.canUndo]
 * @param {'wide'|'column'} [props.layout] the workbench, or the phone's chat
 * @param {(plan: object) => void} props.onChange  every edit, with the whole plan
 * @param {(plan: object) => void} props.onBuild
 * @param {() => void} props.onSkip     build from the request without a plan
 * @param {() => void} props.onRetry    draft again after an error
 * @param {(feedback: string) => void} props.onRevise
 * @param {() => void} [props.onUndo]
 */
export default function PlanBlueprint({
  plan, status, error, prompt, credits = null, canUndo = false, layout = 'wide',
  onChange, onBuild, onSkip, onRetry, onRevise, onUndo,
}) {
  const [feedback, setFeedback] = useState('');
  const [notesOpen, setNotesOpen] = useState(!!plan?.notes);
  const reduceMotion = useReducedMotion();
  const topRef = useRef(null);
  const column = layout === 'column';
  const busy = status === 'revising';

  useEffect(() => { if (plan?.notes) setNotesOpen(true); }, [plan?.notes]);

  // Bring the plan forward when it arrives, rather than leaving it under the
  // fold of whatever pane it landed in.
  useEffect(() => {
    if (status === 'ready') topRef.current?.scrollIntoView?.({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [status, reduceMotion]);

  if (status === 'drafting' || (!plan && status !== 'error')) {
    return <div className={column ? 'py-2' : 'p-6 sm:p-10'}><Drafting prompt={prompt} /></div>;
  }

  if (status === 'error' && !plan) {
    return (
      <div className={column ? 'py-2' : 'p-6 sm:p-10'}>
        <div className="max-w-xl mx-auto rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-5" role="alert">
          <p className="flex items-center gap-2 text-amber-300 font-semibold"><AlertTriangle className="w-4 h-4" /> The plan could not be drafted</p>
          <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed">
            {error || 'Something went wrong drafting it.'} Nothing has been built and no credit was used.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={onRetry} className="h-9 px-4 rounded-full bg-orange-500 hover:bg-orange-400 text-ink text-sm font-semibold inline-flex items-center gap-1.5">
              <RotateCcw className="w-4 h-4" /> Try again
            </button>
            <button onClick={onSkip} className="h-9 px-4 rounded-full border border-zinc-700 text-sm font-semibold text-zinc-300 hover:text-white">
              Build without a plan
            </button>
          </div>
        </div>
      </div>
    );
  }

  const pages = plan.pages || [];
  const features = plan.features || [];
  const taken = new Set(pages.map(p => p.path));

  const set = (patch) => onChange({ ...plan, ...patch });
  const setPage = (i, patch) => set({ pages: pages.map((p, n) => (n === i ? { ...p, ...patch } : p)) });
  const renamePage = (i, label) => {
    const p = pages[i];
    // A page added here gets its file name from its label until it is built.
    if (p.autoPath && p.path !== 'index.html') {
      const others = new Set(pages.filter((_, n) => n !== i).map(x => x.path));
      setPage(i, { label, path: slugPath(label, others) });
    } else {
      setPage(i, { label });
    }
  };
  const movePage = (i, dir) => {
    const j = i + dir;
    if (j < 1 || j >= pages.length || i < 1) return;   // home stays first
    const next = [...pages];
    [next[i], next[j]] = [next[j], next[i]];
    set({ pages: next });
  };
  const removePage = (i) => set({ pages: pages.filter((_, n) => n !== i) });
  const addPage = () => {
    if (pages.length >= PAGE_LIMIT) return;
    // `uid` keys the card: this page's file name follows its label as it is
    // typed, and a key that changed with it would remount the card — and drop
    // the cursor — on every keystroke.
    set({ pages: [...pages, {
      path: slugPath('new page', taken), label: 'New page', purpose: '', sections: [],
      autoPath: true, uid: `new-${Date.now()}-${pages.length}`,
    }] });
  };
  const addSection = (i, text) => {
    const p = pages[i];
    if ((p.sections || []).length >= SECTION_LIMIT) return;
    setPage(i, { sections: [...(p.sections || []), text] });
  };
  const removeSection = (i, j) => setPage(i, { sections: pages[i].sections.filter((_, n) => n !== j) });
  const moveSection = (i, j, dir) => {
    const s = [...(pages[i].sections || [])];
    const k = j + dir;
    if (k < 0 || k >= s.length) return;
    [s[j], s[k]] = [s[k], s[j]];
    setPage(i, { sections: s });
  };

  const submitFeedback = (e) => {
    e.preventDefault();
    const text = feedback.trim();
    if (!text || busy) return;
    onRevise(text);
    setFeedback('');
  };

  const reveal = (i) => (reduceMotion ? {} : {
    initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.04 * i, duration: 0.2 },
  });

  const INPUT = 'w-full bg-transparent outline-none placeholder-zinc-600 focus:bg-zinc-950/60 rounded-md -mx-1 px-1 transition-colors';

  return (
    <div ref={topRef} className={column ? 'pb-2' : 'min-h-full flex flex-col'}>
      <div className={column ? 'space-y-4' : 'flex-1 p-4 sm:p-6 lg:p-8 space-y-5 max-w-6xl w-full mx-auto'}>
        {/* ── Header: what the site is ── */}
        <motion.section {...reveal(0)} className="rounded-2xl border border-zinc-800 bg-zinc-900/80 p-4 sm:p-6">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/10 border border-orange-500/25 px-2.5 py-1 font-semibold text-orange-300">
              <ListChecks className="w-3.5 h-3.5" /> Plan mode
            </span>
            <span className="text-zinc-500">Change anything — it is free. Only Build uses a credit.</span>
            {busy && <span className="inline-flex items-center gap-1.5 text-orange-300"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Updating the plan…</span>}
          </div>

          <input
            value={plan.title || ''}
            onChange={(e) => set({ title: e.target.value.slice(0, 120) })}
            placeholder="Your site's name"
            aria-label="Site name"
            className={`${INPUT} mt-4 text-2xl sm:text-3xl font-extrabold tracking-tight text-white`}
          />
          <textarea
            value={plan.summary || ''}
            onChange={(e) => set({ summary: e.target.value.slice(0, 400) })}
            placeholder="What the site is for, and who it serves"
            aria-label="What the site is for"
            rows={column ? 3 : 2}
            className={`${INPUT} mt-2 text-sm text-zinc-400 leading-relaxed resize-none`}
          />

          <div className={`mt-4 grid gap-3 ${column ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'}`}>
            <label className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-3 py-2.5">
              <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-zinc-500"><Users className="w-3 h-3" /> Audience</span>
              <input value={plan.audience || ''} onChange={(e) => set({ audience: e.target.value.slice(0, 120) })}
                placeholder="Who it is for" className={`${INPUT} mt-1 text-sm text-zinc-200`} />
            </label>
            <label className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-3 py-2.5">
              <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-zinc-500"><Wand2 className="w-3 h-3" /> Style</span>
              <input value={plan.direction || ''} onChange={(e) => set({ direction: e.target.value.slice(0, 120) })}
                placeholder="e.g. warm editorial" className={`${INPUT} mt-1 text-sm text-zinc-200`} />
            </label>
            <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-3 py-2.5">
              <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-zinc-500"><Palette className="w-3 h-3" /> Colours</span>
              <div className="mt-1.5 flex items-center gap-3">
                {[['neutral', 'Base'], ['accent', 'Accent']].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer">
                    <input type="color" value={plan.palette?.[key] || (key === 'accent' ? '#f97316' : '#18181b')}
                      onChange={(e) => set({ palette: { ...(plan.palette || {}), [key]: e.target.value } })}
                      aria-label={`${label} colour`}
                      className="w-7 h-7 rounded-md border border-zinc-700 bg-transparent p-0.5 cursor-pointer" />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500 mb-2">Must-have features</p>
            <div className="flex flex-wrap gap-1.5">
              {features.map((f, i) => (
                <Chip key={`${f}-${i}`} onRemove={() => set({ features: features.filter((_, n) => n !== i) })} removeLabel={`Remove ${f}`}>{f}</Chip>
              ))}
              {features.length < FEATURE_LIMIT && (
                <ChipInput placeholder="e.g. booking form" label="Feature" onAdd={(v) => set({ features: [...features, v] })} />
              )}
            </div>
          </div>
        </motion.section>

        {/* ── The pages ── */}
        <section aria-label="Pages">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2"><FileText className="w-4 h-4 text-zinc-500" /> {pages.length} page{pages.length === 1 ? '' : 's'}</h3>
            {pages.length < PAGE_LIMIT && (
              <button onClick={addPage} className="h-8 px-3 rounded-full border border-zinc-700 text-xs font-semibold text-zinc-300 hover:text-white inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add a page
              </button>
            )}
          </div>
          <div className={`grid gap-3 ${column ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'}`}>
            <AnimatePresence initial={false}>
              {pages.map((p, i) => (
                <motion.div
                  key={p.uid || p.path}
                  layout={!reduceMotion}
                  {...reveal(i + 1)}
                  exit={reduceMotion ? undefined : { opacity: 0, scale: 0.97 }}
                  className="rounded-2xl border border-zinc-800 bg-zinc-900/80 p-4 flex flex-col gap-2.5"
                >
                  <div className="flex items-start gap-2">
                    <span className="w-6 h-6 rounded-lg bg-zinc-800 text-[11px] font-bold text-zinc-400 grid place-items-center shrink-0 mt-0.5">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <input value={p.label || ''} onChange={(e) => renamePage(i, e.target.value.slice(0, 40))}
                        aria-label={`Page ${i + 1} name`} placeholder="Page name"
                        className={`${INPUT} text-sm font-semibold text-white`} />
                      <code className="block text-[10px] text-zinc-600 truncate">{p.path}</code>
                    </div>
                    <div className="flex items-center shrink-0">
                      {i > 0 && (
                        <>
                          <button onClick={() => movePage(i, -1)} disabled={i <= 1} aria-label={`Move ${p.label} up`}
                            className="w-6 h-6 grid place-items-center rounded-md text-zinc-500 hover:text-white disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                          <button onClick={() => movePage(i, 1)} disabled={i === pages.length - 1} aria-label={`Move ${p.label} down`}
                            className="w-6 h-6 grid place-items-center rounded-md text-zinc-500 hover:text-white disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                          <button onClick={() => removePage(i)} aria-label={`Remove ${p.label}`}
                            className="w-6 h-6 grid place-items-center rounded-md text-zinc-500 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                    </div>
                  </div>
                  <input value={p.purpose || ''} onChange={(e) => setPage(i, { purpose: e.target.value.slice(0, 140) })}
                    aria-label={`What ${p.label} is for`} placeholder="What this page is for"
                    className={`${INPUT} text-xs text-zinc-400`} />
                  <ol className="space-y-1">
                    {(p.sections || []).map((s, j) => (
                      <li key={`${s}-${j}`} className="group flex items-center gap-1.5 rounded-lg bg-zinc-950/60 border border-zinc-800 pl-2.5 pr-1 h-7">
                        <span className="flex-1 min-w-0 truncate text-xs text-zinc-300">{s}</span>
                        <button onClick={() => moveSection(i, j, -1)} disabled={j === 0} aria-label={`Move ${s} up`}
                          className="w-5 h-5 grid place-items-center text-zinc-600 hover:text-white disabled:opacity-20 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"><ChevronUp className="w-3 h-3" /></button>
                        <button onClick={() => moveSection(i, j, 1)} disabled={j === p.sections.length - 1} aria-label={`Move ${s} down`}
                          className="w-5 h-5 grid place-items-center text-zinc-600 hover:text-white disabled:opacity-20 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"><ChevronDown className="w-3 h-3" /></button>
                        <button onClick={() => removeSection(i, j)} aria-label={`Remove ${s}`}
                          className="w-5 h-5 grid place-items-center text-zinc-600 hover:text-red-400"><X className="w-3 h-3" /></button>
                      </li>
                    ))}
                  </ol>
                  {(p.sections || []).length < SECTION_LIMIT && (
                    <ChipInput placeholder="Add a section" label={`Section for ${p.label || 'this page'}`} onAdd={(v) => addSection(i, v)} />
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </section>

        {/* ── Notes: the user's own words, carried to the build verbatim ── */}
        <section className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4">
          {notesOpen ? (
            <label className="block">
              <span className="text-xs font-semibold text-zinc-300">Anything else the builder should know?</span>
              <textarea value={plan.notes || ''} onChange={(e) => set({ notes: e.target.value.slice(0, 1000) })} rows={3}
                placeholder="Real details make a better site: your business name, prices, opening hours, phone number, the tone you want…"
                className="mt-2 w-full rounded-xl border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 outline-none focus:border-zinc-600 resize-none leading-relaxed" />
            </label>
          ) : (
            <button onClick={() => setNotesOpen(true)} className="text-xs font-semibold text-zinc-400 hover:text-white inline-flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Add your own notes — prices, hours, contact details
            </button>
          )}
        </section>

        {/* ── Revise by describing the change ── */}
        {!column && (
          <form onSubmit={submitFeedback} className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-2 flex items-center gap-2">
            <input value={feedback} onChange={(e) => setFeedback(e.target.value.slice(0, 500))} disabled={busy}
              placeholder="Ask for a change to the plan — “add a pricing page”, “make it feel more premium”…"
              aria-label="Ask for a change to the plan"
              className="flex-1 min-w-0 h-10 bg-transparent text-sm text-white placeholder-zinc-600 outline-none" />
            {canUndo && (
              <button type="button" onClick={onUndo} disabled={busy} title="Undo the last change"
                className="h-9 px-3 rounded-xl text-xs font-semibold text-zinc-400 hover:text-white inline-flex items-center gap-1 disabled:opacity-40">
                <Undo2 className="w-3.5 h-3.5" /> Undo
              </button>
            )}
            <button type="submit" disabled={busy || !feedback.trim()}
              className="h-9 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-xs font-semibold text-white disabled:opacity-40 inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Update plan
            </button>
          </form>
        )}
        {status === 'error' && error && (
          <p role="alert" className="text-sm text-amber-300 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {error}</p>
        )}
      </div>

      {/* ── Build ── sticky, so it is always in reach however long the plan */}
      <div className={`sticky bottom-0 z-10 border-t border-zinc-800 bg-zinc-950/95 backdrop-blur ${column ? '-mx-3 px-3 py-3 mt-2' : 'px-4 sm:px-6 lg:px-8 py-3'}`}>
        <div className={`flex flex-wrap items-center gap-2 ${column ? '' : 'max-w-6xl mx-auto'}`}>
          <p className="text-[11px] text-zinc-500 mr-auto flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-orange-400" />
            Planning is free · building uses 1 credit{typeof credits === 'number' ? ` · ${credits} left` : ''}
          </p>
          <button onClick={onSkip} disabled={busy} className="h-9 px-3 rounded-full text-xs font-semibold text-zinc-400 hover:text-white disabled:opacity-40">
            Skip plan
          </button>
          <button onClick={onRetry} disabled={busy} title="Draft a fresh plan from your original request"
            className="h-9 px-3 rounded-full border border-zinc-700 text-xs font-semibold text-zinc-300 hover:text-white disabled:opacity-40 inline-flex items-center gap-1">
            <RotateCcw className="w-3.5 h-3.5" /> Redraft
          </button>
          <button onClick={() => onBuild(plan)} disabled={busy || !pages.length}
            className="h-10 px-5 rounded-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-ink text-sm font-bold inline-flex items-center gap-1.5 shadow-[0_0_20px_rgba(249,115,22,0.25)]">
            Build this site <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
